import React, { forwardRef, useImperativeHandle } from 'react';
import { Modal } from 'antd';
import styled from 'styled-components';
import { buildScopeVars, SCHEDULE_ACCENT } from 'TimeOffList/theme';
import { IconX } from 'TimeOffList/components/icons';
import { useBatchForm } from './hooks/use-batch-form';
import StaffSelectStrip from './components/StaffSelectStrip';
import DateCalendar from './components/DateCalendar';
import TimeSection from './components/TimeSection';
import ConflictWarning from './components/ConflictWarning';
import ReasonSection from './components/ReasonSection';
import SelectedPanel from './components/SelectedPanel';

export interface ScheduleTimeOffBatchRef {
  open: () => void;
  close: () => void;
}

export const scheduleTimeOffBatchRef = React.createRef<ScheduleTimeOffBatchRef>();

const ScheduleTimeOffBatch = forwardRef<ScheduleTimeOffBatchRef>((_props, ref) => {
  const f = useBatchForm();

  useImperativeHandle(ref, () => ({ open: f.openModal, close: f.closeModal }));

  return (
    <ModalStyled
      open={f.isOpen}
      onCancel={f.closeModal}
      closable={false}
      footer={null}
      centered
      width={1320}
      styles={{
        content: { padding: 0, borderRadius: 18, overflow: 'hidden' },
        body: { padding: 0 },
        mask: { background: 'rgba(0,24,72,0.45)' },
      }}
    >
      <Root>
        <Header>
          <div className="left" />
          <span className="title">Add Schedule Time Off</span>
          <div className="right">
            <CloseBtn onClick={f.closeModal} title="Close" aria-label="Close">
              <IconX size={20} strokeWidth={2.6} />
            </CloseBtn>
          </div>
        </Header>

        <Body>
          <LeftSection>
            <StaffSelectStrip
              staffs={f.staffs}
              selectedStaffIds={f.selectedStaffIds}
              onToggle={f.toggleStaff}
              onSelectAll={f.selectAllStaff}
              onClear={f.clearStaff}
            />
            <CalForm>
              <DateCalendar
                viewYear={f.viewYear}
                viewMonth={f.viewMonth}
                rangeStart={f.rangeStart}
                rangeEnd={f.rangeEnd}
                pickPhase={f.pickPhase}
                onPickDay={f.pickDay}
                onChangeFrom={f.setRangeFrom}
                onChangeTo={f.setRangeTo}
                onPrevMonth={f.prevMonth}
                onNextMonth={f.nextMonth}
                onToday={f.pickToday}
                onTomorrow={f.pickTomorrow}
              />
              <FormColumn>
                <TimeSection
                  allDay={f.allDay}
                  fromMin={f.fromMin}
                  toMin={f.toMin}
                  openDrop={f.openDrop}
                  timeStepMin={f.timeStepMin}
                  onToggleAllDay={f.toggleAllDay}
                  onStep={f.step}
                  onToggleDrop={f.toggleDrop}
                  onPickTime={f.pickTime}
                />
                <ConflictWarning conflicts={f.conflicts} />
                <ReasonSection
                  reasonChip={f.reasonChip}
                  reasonText={f.reasonText}
                  onPickChip={f.setReasonChip}
                  onChangeText={f.setReasonText}
                  addHint={f.addHint}
                  addLabel={f.addLabel}
                  onAdd={f.addRecords}
                />
              </FormColumn>
            </CalForm>
          </LeftSection>

          <SelectedPanel
            records={f.records}
            indexById={f.indexById}
            avatarById={f.avatarById}
            creating={f.creating}
            onRemove={f.removeRecord}
            onCancel={f.closeModal}
            onCreate={f.createAll}
          />
        </Body>
      </Root>
    </ModalStyled>
  );
});

ScheduleTimeOffBatch.displayName = 'ScheduleTimeOffBatch';

export default ScheduleTimeOffBatch;

const ModalStyled = styled(Modal)`
  .ant-modal-content { padding: 0; }
`;

const Root = styled.div`
  ${buildScopeVars(SCHEDULE_ACCENT)}
  display: flex;
  flex-direction: column;
  height: min(880px, 88vh);
  max-height: 88vh;
  overflow: hidden;
  background: var(--bg-surface);
`;

// Same modal-header style as the Time Off List: light blue band, blue bottom
// rule, centered dark title, orange close action.
const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 16px;
  background: #E8F3FF;
  border-bottom: 1px solid #148CF1;
  flex-shrink: 0;
  .left { display: flex; align-items: center; }
  .right { display: flex; align-items: center; gap: 8px; }
  .title {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    color: #1D2129;
    text-align: center;
    font-family: Poppins, inherit;
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
    text-transform: uppercase;
    white-space: nowrap;
  }
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 34px;
  border: none;
  border-radius: 5px;
  background: #fff;
  color: #F05326;
  cursor: pointer;
  &:hover { background: #FFEFEB; }
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const LeftSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--border-1);
`;

const CalForm = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const FormColumn = styled.div`
  flex: 1;
  min-width: 0;
  padding: 20px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
